/**
 * UpcomingByCompetition.jsx
 * Shared "what's coming up" list, grouped by competition instead of shown as
 * a flat date-ordered table — see docs_zenanalytics/05-SCHEDULE-MILESTONES-SPEC-2026-09-25.md.
 * At real volume (a competitive racket-sport player can easily play 50-100+
 * matches/year across several concurrent leagues/tournaments) a flat list of
 * everything upcoming stops being scannable; grouping by the competition it
 * belongs to lets an athlete find "what's next in the thing I actually care
 * about right now" instead of scanning dates.
 *
 * Used at two horizons from the same component: Home's "What's Next" passes
 * a short horizonDays + collapsed groups (a digest), the Game Plan tab passes
 * no horizon + expanded groups (the full planning workspace).
 */
import React, { useState, useMemo } from 'react';
import { opponentLabel, formatBadge } from '../utils/matchFormat';
import { onEnterOrSpace } from '../utils/a11y';
import { toLocalISO, todayLocal, formatDate } from '../utils/dateLocal';

const MATCH_TYPES = ['match', 'competition', 'race_day'];
const TRAINING_TYPES = ['training', 'brick', 'strength', 'recovery', 'other'];

const C = {
  card: 'rgba(15,23,42,0.82)', border: 'rgba(255,255,255,0.10)',
  text: '#e2e8f0', muted: '#b7c2cc', accent: '#3dbfb0',
  font: "'Inter', system-ui, sans-serif",
};

const COMP_ICON = { league: '🏅', tournament: '🏆', practice: '🎯' };

function fmtDate(iso) {
  if (!iso) return '';
  return formatDate(iso);
}

function dueLabel(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr + 'T12:00:00'); d.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  if (days === 0) return { text: 'Today', color: '#ef4444' };
  if (days === 1) return { text: 'Tomorrow', color: '#f59e0b' };
  if (days < 0) return { text: 'Overdue', color: '#ef4444' };
  return { text: `in ${days}d`, color: C.muted };
}

function CompetitionGroup({ comp, items, previewCount, expandedDefault, onOpenPlan }) {
  const [expanded, setExpanded] = useState(!!expandedDefault);
  const visible = expanded || previewCount == null ? items : items.slice(0, previewCount);
  const hiddenCount = items.length - visible.length;

  // Same format-aware progress line as the full Competition detail view —
  // a short version here since this is a digest, not the full workspace.
  const totalPlanned = comp?.format_meta?.total_matches_planned;
  const progress = comp?.type === 'league' && totalPlanned
    ? `next of ${totalPlanned} planned`
    : null;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4, padding: '0 2px' }}>
        <span style={{ fontSize: 13 }}>{comp ? (COMP_ICON[comp.type] || '🏆') : '🎯'}</span>
        <span style={{ color: C.text, fontWeight: 700, fontSize: 12.5 }}>
          {comp ? comp.name : 'Not linked to a competition'}
        </span>
        {progress && <span style={{ color: C.muted, fontSize: 10.5 }}>{progress}</span>}
        {comp?.end_date && (
          <span style={{ color: C.muted, fontSize: 10.5 }}>ends {fmtDate(comp.end_date)}</span>
        )}
      </div>
      {visible.map(p => {
        const due = dueLabel((p.scheduled_date || '').slice(0, 10));
        const isTraining = TRAINING_TYPES.includes(p.type);
        return (
          <div key={p.id} onClick={() => onOpenPlan(p)} role="button" tabIndex={0} onKeyDown={onEnterOrSpace(() => onOpenPlan(p))} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px',
            borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.03)', marginBottom: 3,
          }}>
            <div style={{ color: C.muted, fontSize: 11, width: 44, flexShrink: 0 }}>{fmtDate(p.scheduled_date)}</div>
            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.text, fontSize: 12.5, fontWeight: 600 }}>
              {isTraining ? '🏋️ ' : ''}
              {formatBadge(p) && <span style={{ color: C.muted, fontWeight: 700, marginRight: 4 }}>{formatBadge(p)}</span>}
              {p.opponent_name ? `vs ${opponentLabel(p)}` : (p.round_label || p.goal || p.type)}
            </div>
            <div style={{ color: due.color, fontSize: 10.5, fontWeight: 700, flexShrink: 0 }}>{due.text}</div>
          </div>
        );
      })}
      {hiddenCount > 0 && (
        <button onClick={() => setExpanded(true)} style={{
          background: 'none', border: 'none', color: C.accent, fontSize: 11.5, fontWeight: 600,
          cursor: 'pointer', fontFamily: C.font, padding: '2px 8px',
        }}>
          + {hiddenCount} more →
        </button>
      )}
    </div>
  );
}

export default function UpcomingByCompetition({
  plans, competitions = [], horizonDays = null, previewCount = 2,
  expandedDefault = false, onOpenPlan, emptyHint,
}) {
  const groups = useMemo(() => {
    const todayStr = todayLocal();
    const cutoffStr = horizonDays != null
      ? (() => { const d = new Date(); d.setDate(d.getDate() + horizonDays); return toLocalISO(d); })()
      : null;

    const upcoming = plans.filter(p => {
      if (!(MATCH_TYPES.includes(p.type) || TRAINING_TYPES.includes(p.type))) return false;
      const dateStr = (p.scheduled_date || '').slice(0, 10);
      if (!dateStr || dateStr < todayStr) return false;
      if (cutoffStr && dateStr > cutoffStr) return false;
      // "Upcoming" = not yet logged a result/completion.
      return !(p.result || p.performance_score);
    });

    const compById = {};
    competitions.forEach(c => { compById[c.id] = c; });

    const byComp = {};
    const unlinked = [];
    upcoming.forEach(p => {
      const comp = p.competition_id ? compById[p.competition_id] : null;
      if (comp) {
        (byComp[comp.id] = byComp[comp.id] || { comp, items: [] }).items.push(p);
      } else {
        unlinked.push(p);
      }
    });

    const sortByDate = (a, b) => (a.scheduled_date || '').localeCompare(b.scheduled_date || '');
    const compGroups = Object.values(byComp).map(g => ({ ...g, items: g.items.sort(sortByDate) }));
    compGroups.sort((a, b) => sortByDate(a.items[0], b.items[0]));

    const result = compGroups.map(g => ({ comp: g.comp, items: g.items }));
    if (unlinked.length > 0) {
      result.push({ comp: null, items: unlinked.sort(sortByDate) });
    }
    return result;
  }, [plans, competitions, horizonDays]);

  if (groups.length === 0) {
    return (
      <div style={{ color: C.muted, fontSize: 12.5, padding: '10px 2px' }}>
        {emptyHint || 'Nothing planned yet.'}
      </div>
    );
  }

  return (
    <div>
      {groups.map((g, i) => (
        <CompetitionGroup
          key={g.comp?.id || `unlinked-${i}`}
          comp={g.comp}
          items={g.items}
          previewCount={previewCount}
          expandedDefault={expandedDefault}
          onOpenPlan={onOpenPlan}
        />
      ))}
    </div>
  );
}
